import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { compose } from 'redux';
import omit from 'lodash/omit';
import { useNavigate } from 'react-router-dom';
import createCommunityMemberService from '../../../../portal/community/members/services/createCommunityMemberService';
import CommunityMemberForm, {
  DEFAULT_TEMPLATE_ID,
} from '../../../../portal/community/members/components/CommunityMemberForm';
import joinRequestService from '../../services/joinRequests/joinRequestService';

const AcceptJoinRequest = ({
  open,
  communityId,
  rootUrl,
  createCommunityMemberFetch,
  dispatchCreateCommunityMemberPost,
  dispatchJoinRequestGet,
  joinRequestFetch,
  isCommunityView,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { actionId } = useParams();

  useEffect(() => {
    if (open && actionId) {
      dispatchJoinRequestGet(actionId, () => navigate(rootUrl), t);
    }
  }, [t, open, actionId, dispatchJoinRequestGet, rootUrl, navigate]);

  const handleCreateCommunityMember = useCallback(
    values => {
      let body = values;
      if (body.onboardingTemplateId === DEFAULT_TEMPLATE_ID) {
        body = omit(values, ['onboardingTemplateId']);
      }
      dispatchCreateCommunityMemberPost(
        communityId,
        {
          ...body,
          organisation: body.organisation,
          principal: {
            ...body.principal,
            inviteRequest: {
              sendInvitation: true,
              invitationType: 1,
              ...body.principal.inviteRequest,
            },
          },
        },
        () => navigate(rootUrl),
        undefined,
        t,
        joinRequestFetch.value?.id
      );
    },
    [rootUrl, dispatchCreateCommunityMemberPost, communityId, t, joinRequestFetch.value, navigate]
  );

  const defaultData = useMemo(() => {
    if (!joinRequestFetch.value) return null;

    return {
      joinRequestId: joinRequestFetch.value?.id,
      organisation: {
        name: joinRequestFetch.value?.organisationName,
        city: joinRequestFetch.value?.city,
        country: joinRequestFetch.value?.country,
      },
      identificationCodes: (joinRequestFetch.value?.identificationCodes || []).map(
        identificationCode => ({
          identificationCodeTypeTag: identificationCode.typeTag,
          value: identificationCode.value,
        })
      ),
      principal: {
        fullName: joinRequestFetch.value?.fullName,
        primaryEmailAddress: joinRequestFetch.value?.emailAddress,
        languageId: joinRequestFetch.value?.language,
        timeZone: joinRequestFetch.value?.timeZone,
        inviteRequest: {
          freeText: t('joinRequestAcceptedInfo', { fullName: joinRequestFetch.value?.fullName }),
        },
      },
    };
  }, [joinRequestFetch.value, t]);

  if (!open) return null;

  return (
    <CommunityMemberForm
      header={t('createCommunityMember')}
      onSubmit={handleCreateCommunityMember}
      communityId={communityId}
      submitProcessing={createCommunityMemberFetch.pending}
      dataLoading={joinRequestFetch.pending}
      data={defaultData}
      returnUrl={rootUrl}
      isCommunityView={isCommunityView}
    />
  );
};

AcceptJoinRequest.propTypes = {
  dispatchCreateCommunityMemberPost: PropTypes.func.isRequired,
  createCommunityMemberFetch: PropTypes.object.isRequired,
  dispatchJoinRequestGet: PropTypes.func.isRequired,
  joinRequestFetch: PropTypes.object.isRequired,
  communityId: PropTypes.string.isRequired,
  rootUrl: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  isCommunityView: PropTypes.bool,
};

AcceptJoinRequest.defaultProps = {
  isCommunityView: false,
};

export default compose(joinRequestService, createCommunityMemberService)(AcceptJoinRequest);
